import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  getFirestore,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  collection,
  query,
  orderBy,
  where,
  limit,
  startAfter,
  onSnapshot,
  serverTimestamp,
  runTransaction,
  getDocs,
  increment,
  Unsubscribe,
  DocumentSnapshot,
  QueryDocumentSnapshot,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import type { RootState } from "./store";

// ─────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────

export interface SupportMessage {
  id: string;
  senderId: string;
  senderName: string;
  senderProfilePicture: string | null;
  text: string;
  timestamp: number | null;
  status: "sending" | "sent" | "failed";
  optimistic?: boolean;
}

export interface SupportThread {
  id: string;
  ticketId: string;
  userId: string;
  agentId: string;
  unread: Record<string, number>;
  lastMessage: Omit<SupportMessage, "optimistic"> | null;
  updatedAt: number | null;
  status: "open" | "closed";
}

export interface SupportAgent {
  id: string;
  firstName: string;
  lastName: string;
  profilePicture: string | null;
  isAvailable: boolean;
  load: number;
}

interface SupportState {
  thread: SupportThread | null;
  messages: SupportMessage[];
  allThreads: SupportThread[];
  availableAgents: SupportAgent[];
  agentsLoading: boolean;
  loading: boolean;
  sendingIds: string[];
  error: string | null;
  hasMore: boolean;
  paginating: boolean;
}

const initialState: SupportState = {
  thread: null,
  messages: [],
  allThreads: [],
  availableAgents: [],
  agentsLoading: false,
  loading: false,
  sendingIds: [],
  error: null,
  hasMore: true,
  paginating: false,
};

// ─────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────

const PAGE_SIZE = 30;

// ─────────────────────────────────────────
// TICKET ID GENERATOR
// Format: TKT-XXXXXXXX (8 uppercase hex chars)
// ─────────────────────────────────────────

const generateTicketId = (): string => {
  const hex =
    typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function"
      ? Array.from(crypto.getRandomValues(new Uint8Array(4)))
          .map((b) => b.toString(16).padStart(2, "0"))
          .join("")
          .toUpperCase()
      : Math.floor(Math.random() * 0xffffffff)
          .toString(16)
          .padStart(8, "0")
          .toUpperCase();
  return `TKT-${hex}`;
};

// ─────────────────────────────────────────
// GLOBALS
// ─────────────────────────────────────────

const _unsubs: Map<string, Unsubscribe> = new Map();

function registerUnsub(key: string, unsub: Unsubscribe) {
  _unsubs.get(key)?.();
  _unsubs.set(key, unsub);
}

export function unsubscribeSupport(key?: string) {
  if (key) {
    _unsubs.get(key)?.();
    _unsubs.delete(key);
  } else {
    _unsubs.forEach((fn) => fn());
    _unsubs.clear();
  }
}

// ─────────────────────────────────────────
// CURSOR REGISTRY
// ─────────────────────────────────────────

let _lastMessageDoc: QueryDocumentSnapshot | null = null;

// ─────────────────────────────────────────
// DB / AUTH
// ─────────────────────────────────────────

const db   = getFirestore();
const auth = getAuth();

// ─────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────

const serializeDoc = (snap: DocumentSnapshot) => {
  const d = snap.data();
  if (!d) return null;
  return {
    ...d,
    updatedAt: d.updatedAt?.toMillis?.() ?? null,
    lastMessage: d.lastMessage
      ? {
          ...d.lastMessage,
          timestamp: d.lastMessage.timestamp?.toMillis?.() ?? null,
        }
      : null,
  };
};

const serializeQueryDoc = (d: QueryDocumentSnapshot) => ({
  id: d.id,
  ...((() => {
    const data = d.data();
    return {
      ...data,
      updatedAt: data.updatedAt?.toMillis?.() ?? null,
      lastMessage: data.lastMessage
        ? {
            ...data.lastMessage,
            timestamp: data.lastMessage.timestamp?.toMillis?.() ?? null,
          }
        : null,
    };
  })()),
});

const serializeMessage = (snap: QueryDocumentSnapshot): SupportMessage => {
  const d = snap.data();
  return {
    id: snap.id,
    senderId: d.senderId ?? "",
    senderName: d.senderName ?? "",
    senderProfilePicture: d.senderProfilePicture ?? null,
    text: d.text ?? "",
    timestamp: d.timestamp?.toMillis?.() ?? null,
    status: "sent",
  };
};

const pickLeastLoadedAgent = async (): Promise<SupportAgent> => {
  const q = query(
    collection(db, "supportAgents"),
    where("isAvailable", "==", true)
  );
  const snap = await getDocs(q);
  if (snap.empty) {
    throw new Error("No available support agents at this time. Please try again shortly.");
  }
  const agents = snap.docs
    .map((d) => ({ docId: d.id, ...d.data() } as any))
    .sort((a, b) => (a.load ?? 0) - (b.load ?? 0));
  const best = agents[0];
  return {
    id: best.docId,
    firstName: best.firstName ?? "",
    lastName: best.lastName ?? "",
    profilePicture: best.profilePicture ?? null,
    isAvailable: best.isAvailable ?? true,
    load: best.load ?? 0,
  };
};

// ─────────────────────────────────────────
// THUNKS
// ─────────────────────────────────────────

export const listenToAvailableAgents = createAsyncThunk<
  void, void, { state: RootState }
>("support/listenAvailableAgents", async (_, { dispatch }) => {
  const q = query(
    collection(db, "supportAgents"),
    where("isAvailable", "==", true)
  );
  const unsub = onSnapshot(
    q,
    (snapshot) => {
      const agents: SupportAgent[] = snapshot.docs
        .map((d) => {
          const data = d.data();
          return {
            id: d.id,
            firstName: data.firstName ?? "",
            lastName: data.lastName ?? "",
            profilePicture: data.profilePicture ?? null,
            isAvailable: data.isAvailable ?? true,
            load: data.load ?? 0,
          };
        })
        .sort((a, b) => a.load - b.load);
      dispatch(setAvailableAgents(agents));
    },
    (error) => {
      console.error("[support] listenToAvailableAgents error:", error.message);
      dispatch(setAvailableAgents([]));
    }
  );
  registerUnsub("availableAgents", unsub);
});

export const createOrGetSupportThread = createAsyncThunk<
  SupportThread, void, { state: RootState }
>("support/createOrGetThread", async (_, { rejectWithValue }) => {
  try {
    const userId = auth.currentUser?.uid;
    if (!userId) throw new Error("User not authenticated.");

    const threadId  = `support_${userId}`;
    const threadRef = doc(db, "supportMessenger", threadId);
    const snap      = await getDoc(threadRef);

    if (snap.exists()) {
      return { id: threadId, ...serializeDoc(snap) } as SupportThread;
    }

    const ticketId = generateTicketId();
    const agent    = await pickLeastLoadedAgent();
    const agentRef = doc(db, "supportAgents", agent.id);

    await runTransaction(db, async (tx) => {
      tx.set(threadRef, {
        ticketId,
        userId,
        agentId: agent.id,
        unread: { [userId]: 0, [agent.id]: 0 },
        lastMessage: null,
        status: "open",
        updatedAt: serverTimestamp(),
      });
      tx.update(agentRef, { load: increment(1) });
    });

    const created = await getDoc(threadRef);
    return { id: threadId, ...serializeDoc(created) } as SupportThread;
  } catch (err: any) {
    return rejectWithValue(err.message);
  }
});

export const sendSupportMessage = createAsyncThunk<
  void,
  { text: string; optimisticId: string },
  { state: RootState }
>(
  "support/sendMessage",
  async ({ text, optimisticId }, { getState, dispatch, rejectWithValue }) => {
    try {
      const state  = getState();
      const thread = state.support.thread;
      const sender = state.auth.user;

      if (!thread)         throw new Error("Thread not initialised.");
      if (!sender?.userId) throw new Error("User not authenticated.");

      const messagesRef = collection(db, "supportMessenger", thread.id, "messages");
      const msgDoc = await addDoc(messagesRef, {
        senderId: sender.userId,
        senderName: sender.firstName ?? "User",
        senderProfilePicture: sender.profilePicture ?? null,
        text,
        status: "sent",
        timestamp: serverTimestamp(),
      });

      const receiverId =
        sender.userId === thread.userId ? thread.agentId : thread.userId;

      await updateDoc(doc(db, "supportMessenger", thread.id), {
        lastMessage: {
          id: msgDoc.id,
          senderId: sender.userId,
          senderName: sender.firstName ?? "User",
          senderProfilePicture: sender.profilePicture ?? null,
          text,
          timestamp: serverTimestamp(),
          status: "sent",
        },
        updatedAt: serverTimestamp(),
        [`unread.${sender.userId}`]: 0,
        [`unread.${receiverId}`]: increment(1),
      });

      dispatch(confirmOptimisticMessage({ optimisticId, confirmedId: msgDoc.id }));
    } catch (err: any) {
      dispatch(failOptimisticMessage({ optimisticId }));
      return rejectWithValue(err.message);
    }
  }
);

export const listenToSupportMessages = createAsyncThunk<
  void, { threadId: string }, { state: RootState }
>("support/listenMessages", async ({ threadId }, { dispatch }) => {
  const messagesRef = collection(db, "supportMessenger", threadId, "messages");
  const q = query(messagesRef, orderBy("timestamp", "desc"), limit(PAGE_SIZE));
  const unsub = onSnapshot(q, (snapshot) => {
    if (!snapshot.empty) {
      _lastMessageDoc = snapshot.docs[snapshot.docs.length - 1];
    }
    dispatch(setRealtimeMessages(snapshot.docs.map(serializeMessage).reverse()));
  });
  registerUnsub(`messages_${threadId}`, unsub);
});

export const fetchOlderMessages = createAsyncThunk<
  SupportMessage[], { threadId: string }, { state: RootState }
>("support/fetchOlder", async ({ threadId }, { rejectWithValue }) => {
  try {
    if (!_lastMessageDoc) return [];
    const q = query(
      collection(db, "supportMessenger", threadId, "messages"),
      orderBy("timestamp", "desc"),
      startAfter(_lastMessageDoc),
      limit(PAGE_SIZE)
    );
    const snap = await getDocs(q);
    if (!snap.empty) _lastMessageDoc = snap.docs[snap.docs.length - 1];
    return snap.docs.map(serializeMessage).reverse();
  } catch (err: any) {
    return rejectWithValue(err.message);
  }
});

export const listenToSupportThread = createAsyncThunk<
  void, { threadId: string }, { state: RootState }
>("support/listenThread", async ({ threadId }, { dispatch }) => {
  const unsub = onSnapshot(doc(db, "supportMessenger", threadId), (snap) => {
    if (!snap.exists()) return;
    dispatch(patchThread({ id: threadId, ...serializeDoc(snap) } as SupportThread));
  });
  registerUnsub(`thread_${threadId}`, unsub);
});

export const listenToAgentThreads = createAsyncThunk<
  void, { agentId: string }, { state: RootState }
>("support/listenAgentThreads", async ({ agentId }, { dispatch }) => {
  const q = query(
    collection(db, "supportMessenger"),
    where("agentId", "==", agentId),
    orderBy("updatedAt", "desc")
  );
  const unsub = onSnapshot(q, (snapshot) => {
    const threads: SupportThread[] = snapshot.docs.map((d) => ({
      id: d.id,
      ...(serializeDoc(d) as any),
    }));
    dispatch(setAllThreads(threads));
  });
  registerUnsub(`agentThreads_${agentId}`, unsub);
});

export const listenToOpenSupport = createAsyncThunk<
  void, void, { state: RootState }
>("support/listenToOpenSupport", async (_, { dispatch }) => {
  const q = query(
    collection(db, "supportMessenger"),
    where("status", "==", "open")
  );
  const unsub = onSnapshot(
    q,
    (snapshot) => {
      const threads: SupportThread[] = snapshot.docs
        .map((d) => serializeQueryDoc(d) as SupportThread)
        .sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0));
      dispatch(setAllThreads(threads));
    },
    (error) => console.error("[support] listenToOpenSupport error:", error.message)
  );
  registerUnsub("openThreads", unsub);
});

export const listenToAllSupportThreads = createAsyncThunk<
  void, void, { state: RootState }
>("support/listenToAllSupportThreads", async (_, { dispatch }) => {
  const q = query(collection(db, "supportMessenger"));
  const unsub = onSnapshot(
    q,
    (snapshot) => {
      const threads: SupportThread[] = snapshot.docs
        .map((d) => serializeQueryDoc(d) as SupportThread)
        .sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0));
      dispatch(setAllThreads(threads));
    },
    (error) => console.error("[support] listenToAllSupportThreads error:", error.message)
  );
  registerUnsub("allThreads", unsub);
});

// ── one-time fetch by ticketId ────────────────────────────────
export const fetchThreadByTicketId = createAsyncThunk<
  SupportThread | null, { ticketId: string }, { state: RootState }
>("support/fetchThreadByTicketId", async ({ ticketId }, { rejectWithValue }) => {
  try {
    const q = query(
      collection(db, "supportMessenger"),
      where("ticketId", "==", ticketId)
    );
    const snap = await getDocs(q);
    if (snap.empty) return null;
    const d = snap.docs[0];
    return serializeQueryDoc(d) as SupportThread;
  } catch (err: any) {
    return rejectWithValue(err.message);
  }
});

// ── realtime listener by ticketId ─────────────────────────────
// Use this on the agent messages page — resolves the thread from the URL
// ticketId and keeps it live. Automatically starts the messages listener
// once the threadId is known.
export const listenToThreadByTicketId = createAsyncThunk<
  void, { ticketId: string }, { state: RootState }
>("support/listenToThreadByTicketId", async ({ ticketId }, { dispatch, rejectWithValue }) => {
  try {
    // ── step 1: find the doc whose ticketId field matches ──────
    const q = query(
      collection(db, "supportMessenger"),
      where("ticketId", "==", ticketId)
    );

    const initialSnap = await getDocs(q);
    if (initialSnap.empty) {
      console.warn("[support] listenToThreadByTicketId: no thread found for", ticketId);
      return;
    }

    const threadDocRef = initialSnap.docs[0].ref;
    const threadId     = initialSnap.docs[0].id;

    // ── step 2: set up realtime listener on the resolved doc ───
    const unsub = onSnapshot(
      threadDocRef,
      (snap) => {
        if (!snap.exists()) return;
        const thread = {
          id: snap.id,
          ...serializeDoc(snap),
        } as SupportThread;
        dispatch(patchThread(thread));
      },
      (error) => {
        console.error("[support] listenToThreadByTicketId snapshot error:", error.message);
      }
    );

    registerUnsub(`ticketThread_${ticketId}`, unsub);

    // ── step 3: also start the messages listener ───────────────
    dispatch(listenToSupportMessages({ threadId }));

  } catch (err: any) {
    return rejectWithValue(err.message);
  }
});

export const markThreadAsRead = createAsyncThunk<
  void, { threadId: string; userId: string }
>("support/markRead", async ({ threadId, userId }, { rejectWithValue }) => {
  try {
    await updateDoc(doc(db, "supportMessenger", threadId), {
      [`unread.${userId}`]: 0,
    });
  } catch (err: any) {
    return rejectWithValue(err.message);
  }
});

export const closeSupportThread = createAsyncThunk<
  void, { threadId: string; agentId: string }
>("support/closeThread", async ({ threadId, agentId }, { rejectWithValue }) => {
  try {
    await updateDoc(doc(db, "supportMessenger", threadId), {
      status: "closed",
      updatedAt: serverTimestamp(),
    });
    await updateDoc(doc(db, "supportAgents", agentId), {
      load: increment(-1),
    });
    unsubscribeSupport(`messages_${threadId}`);
    unsubscribeSupport(`thread_${threadId}`);
  } catch (err: any) {
    return rejectWithValue(err.message);
  }
});

// ─────────────────────────────────────────
// SLICE
// ─────────────────────────────────────────

const supportSlice = createSlice({
  name: "support",
  initialState,
  reducers: {
    addOptimisticMessage(state, action: PayloadAction<SupportMessage>) {
      state.messages.push(action.payload);
      state.sendingIds.push(action.payload.id);
    },

    confirmOptimisticMessage(
      state,
      action: PayloadAction<{ optimisticId: string; confirmedId: string }>
    ) {
      const { optimisticId, confirmedId } = action.payload;
      const msg = state.messages.find((m) => m.id === optimisticId);
      if (msg) { msg.id = confirmedId; msg.status = "sent"; msg.optimistic = false; }
      state.sendingIds = state.sendingIds.filter((id) => id !== optimisticId);
    },

    failOptimisticMessage(state, action: PayloadAction<{ optimisticId: string }>) {
      const msg = state.messages.find((m) => m.id === action.payload.optimisticId);
      if (msg) msg.status = "failed";
      state.sendingIds = state.sendingIds.filter((id) => id !== action.payload.optimisticId);
    },

    setRealtimeMessages(state, action: PayloadAction<SupportMessage[]>) {
      const incomingIds = new Set(action.payload.map((m) => m.id));

      // keep optimistic messages whose ID hasn't been confirmed yet
      const pendingOptimistic = state.messages.filter(
        (m) => m.optimistic && !incomingIds.has(m.id)
      );

      // merge: confirmed messages + still-pending optimistic
      const merged = [...action.payload, ...pendingOptimistic];

      // deduplicate by ID — last write wins (confirmed beats optimistic)
      const seen = new Map<string, SupportMessage>();
      for (const msg of merged) {
        const existing = seen.get(msg.id);
        // prefer confirmed (non-optimistic) over optimistic
        if (!existing || (!msg.optimistic && existing.optimistic)) {
          seen.set(msg.id, msg);
        }
      }

      // sort by timestamp ascending so chat reads top→bottom
      state.messages = Array.from(seen.values()).sort(
        (a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0)
      );
    },

    patchThread(state, action: PayloadAction<SupportThread>) {
      state.thread = action.payload;
    },

    setAllThreads(state, action: PayloadAction<SupportThread[]>) {
      state.allThreads = action.payload;
    },

    setAvailableAgents(state, action: PayloadAction<SupportAgent[]>) {
      state.availableAgents = action.payload;
    },

    clearSupport() {
      unsubscribeSupport();
      _lastMessageDoc = null;
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(createOrGetSupportThread.pending,   (state) => { state.loading = true; state.error = null; })
      .addCase(createOrGetSupportThread.fulfilled, (state, action) => { state.thread = action.payload; state.loading = false; })
      .addCase(createOrGetSupportThread.rejected,  (state, action) => { state.loading = false; state.error = action.payload as string; });

    builder
      .addCase(fetchOlderMessages.pending,   (state) => { state.paginating = true; })
      .addCase(fetchOlderMessages.fulfilled, (state, action) => {
        state.paginating = false;
        if (action.payload.length < PAGE_SIZE) state.hasMore = false;
        const existingIds = new Set(state.messages.map((m) => m.id));
        const fresh = action.payload.filter((m) => !existingIds.has(m.id));
        state.messages = [...fresh, ...state.messages];
      })
      .addCase(fetchOlderMessages.rejected, (state) => { state.paginating = false; });

    builder.addCase(closeSupportThread.fulfilled, (state) => {
      if (state.thread) state.thread.status = "closed";
    });

    builder
      .addCase(listenToAvailableAgents.pending,   (state) => { state.agentsLoading = true; })
      .addCase(listenToAvailableAgents.fulfilled, (state) => { state.agentsLoading = false; })
      .addCase(listenToAvailableAgents.rejected,  (state) => { state.agentsLoading = false; });

    builder
      .addCase(listenToOpenSupport.pending,   (state) => { state.loading = true; })
      .addCase(listenToOpenSupport.fulfilled, (state) => { state.loading = false; })
      .addCase(listenToOpenSupport.rejected,  (state) => { state.loading = false; });

    builder
      .addCase(listenToAllSupportThreads.pending,   (state) => { state.loading = true; })
      .addCase(listenToAllSupportThreads.fulfilled, (state) => { state.loading = false; })
      .addCase(listenToAllSupportThreads.rejected,  (state) => { state.loading = false; });

    builder
      .addCase(fetchThreadByTicketId.pending,   (state) => { state.loading = true; })
      .addCase(fetchThreadByTicketId.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) state.thread = action.payload;
      })
      .addCase(fetchThreadByTicketId.rejected,  (state) => { state.loading = false; });

    builder
      .addCase(listenToThreadByTicketId.pending,   (state) => { state.loading = true; })
      .addCase(listenToThreadByTicketId.fulfilled, (state) => { state.loading = false; })
      .addCase(listenToThreadByTicketId.rejected,  (state) => { state.loading = false; });
  },
});

export const {
  addOptimisticMessage,
  confirmOptimisticMessage,
  failOptimisticMessage,
  setRealtimeMessages,
  patchThread,
  setAllThreads,
  setAvailableAgents,
  clearSupport,
} = supportSlice.actions;

export default supportSlice.reducer;